import { Fragment, useEffect, useRef, useState } from "react";
import { type User } from "~/domains/navigation/types/NavigationTypes";
import SimpeRenderServerSideContent from "~/common/components/SimpeRenderServerSideContent";
import PageHeaderElement from "~/common/components/PageHeaderElement";
import DesktopPersonalNav from "~/features/navigation/components/headerMenu/DesktopPersonalNav";
import { userIsCustomer, userIsLoggedIn, userIsRegistered } from "~/domains/user/utils/roles";
import { MediasLogo } from "~/features/navigation/components/TopBar/MediasLogo";
import SearchBox from "~/features/search/components/Autosuggest/SearchBox";
import { CompanyLogo } from "~/domains/company/components/CompanyLogo";
import { getMediasVariant } from "~/features/navigation/components/TopBar/HeaderCircle";

import { UserNavigation } from "~/features/navigation/components/TopBar/UserNavigation";
import { HeaderLoginElement } from "~/features/navigation/components/TopBar/HeaderLoginElement";
import { Box, Divider, IconButton, styled } from "~/common/mui";
import { MobilePersonalNav } from "~/features/navigation/components/headerMenu/MobilePersonalNav";
import { useAffiliateTracking } from "~/domains/affiliate/hooks/useAffiliateTracking";
import { ScrollToTop } from "~/common/components/ScrollToTop/ScrollToTop";
import { isDebugLanguageEnabled } from "~/utils/language/isDebugLanguageEnabled";
import { DebugLanguageToggle } from "~/common/components/DebugLanguageToggle";
import { CURRENT_LANGUAGE } from "~/app/config/appConstants";
import { useAppConfig } from "~/app/config/useAppConfig";
import { Link, LinkType } from "~/common/components/Link";
import { SCAN_DMC_PAGE } from "~/utils/url/staticUrls";
import GlobalMessage from "~/features/navigation/components/GlobalMessage";
import { MergeCustomerCart } from "~/features/cart/components/MergeCustomerCart";
import classNames from "classnames";
import { isCheckoutPage, isDatasheetPage, isForkPage } from "~/utils/url/urlUtils";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { useLogoutMessageHandler } from "~/features/auth/hooks/useLogoutMessageHandler";
import RedirectToPDPComponent from "~/bridges/header/Redirect/RedirectToPDPComponent";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { useTranslation } from "react-i18next";
import { ProductFruits } from "react-product-fruits";
import { Snackbar, SnackbarCloseButton } from "~/common/mui/Snackbar/Snackbar";
import { CategoryNavigation } from "~/features/categoryNavigation/components/CategoryNavigation";
import CheckoutHeader from "~/features/Checkout/components/CheckoutHeader";
import { LanguageSelector } from "~/features/categoryNavigation/components/LanguageSelector";
import QrCodeScannerOutlinedIcon from "@mui/icons-material/QrCodeScannerOutlined";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { useCommerceContext, useSessionUser } from "~/app/config/useSession";
import { StaticPageHeader } from "~/features/navigation/components/StaticPageHeader/StaticPageHeader";
import { SessionExpirePopupContainer } from "~/features/auth/session/useSessionExpirePopup";

export interface Props {
  user?: User;
  isSearchPage: boolean;
  showEdit: boolean;
  includeSessionExpirePopup?: boolean;
}

/**
 *   The entire navigation on top of the page. Consists of three parts:
 *   * Top bar with search bar, company logo etc.
 *   * Middle navigation in HeaderMenu
 */
export const HeaderNavigation = ({ user, showEdit, isSearchPage }: Props) => {
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const selectedAccount = useCommerceContext()?.selectedAccount;
  const effectiveUser: User | undefined = user ?? (sessionUser ? { ...sessionUser, selectedAccount } : undefined);
  // Anonymous - logged out. Go public - logged out when go public is enabled.
  const loggedIn = userIsLoggedIn();

  const mediasVariant = getMediasVariant();

  const screenSize = useNumericScreenSize();

  const isMinPhone = screenSize >= useNumericScreenSize.phone;
  const isMinTablet = screenSize >= useNumericScreenSize.tablet;
  const isMinDesktop = screenSize >= useNumericScreenSize.desktopS;

  const menuAnchorRef = useRef<HTMLDivElement | null>(null);

  const { val: topBar, setTrue: displayTopBar, setFalse: hideTopBar } = useBooleanState(true);

  const showLoginSnackbar = window.sessionStorage.getItem("accountChanged") === "true";
  const showDeleteAccountSuccessSnackbar = window.sessionStorage.getItem("accountDeleted") === "true";

  const { val: overlay, setTrue: showOverlay, setFalse: hideOverlay } = useBooleanState(false);
  const { val: recentSearches, setTrue: showRecentSearches, setFalse: hideRecentSearches } = useBooleanState(false);
  const { val: suggestions, setFalse: hideSuggestions, setTrue: showSuggestions } = useBooleanState(false);
  const {
    val: loginSnackbar,
    setFalse: hideLoginSnackbar,
    setTrue: openLoginSnackbar,
  } = useBooleanState(showLoginSnackbar);

  const { val: deleteAccountSuccessSnackbar, setFalse: hideDeleteAccountSuccessSnackbar } = useBooleanState(
    showDeleteAccountSuccessSnackbar,
  );

  useEffect(() => {
    displayTopBar();
  }, [isMinDesktop]);

  // Tracking in here, because this element is always created
  useAffiliateTracking();
  useLogoutMessageHandler();
  const showDebugLanguage = isDebugLanguageEnabled();

  const applyStickyNav = () => {
    // TODO - Fix this...
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [windowPosition, setWindowPosition] = useState(0);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      const listenScrollEvent = () => {
        setWindowPosition(window.scrollY);
      };
      window.addEventListener("scroll", listenScrollEvent);
      return () => window.removeEventListener("scroll", listenScrollEvent);
    }, []);

    if (windowPosition > 0) {
      return "stickyTop";
    }
  };

  const shouldAddProductFruits =
    (useAppConfig()?.configuration.productFruitsIntegrationEnabled ?? false) && userIsCustomer();
  const categoryNavigation = <CategoryNavigation navigationRef={menuAnchorRef} />;

  return (
    <>
      <GlobalMessage />
      <RedirectToPDPComponent />
      <MergeCustomerCart standardShellReady />
      <Snackbar
        message={t("accountSelection.accountSelectionSuccess")}
        open={loginSnackbar}
        onClose={() => {
          window.sessionStorage.setItem("accountChanged", "false");
          hideLoginSnackbar();
        }}
      />

      <Snackbar
        message={t("feature.deleteAccount.success")}
        open={deleteAccountSuccessSnackbar}
        onClose={() => {
          window.sessionStorage.setItem("accountDeleted", "false");
          hideDeleteAccountSuccessSnackbar();
        }}
        action={
          <SnackbarCloseButton
            onClick={() => {
              window.sessionStorage.setItem("accountDeleted", "false");
              hideDeleteAccountSuccessSnackbar();
            }}
          />
        }
      />

      {shouldAddProductFruits && (
        <ProductFruits
          workspaceCode="lfRfdsZ3tuukhX8r"
          language={(window.__SCHA_BOOT__?.language ?? "en").substring(0, 2)}
          user={{
            username: effectiveUser?.uid ?? "",
            role: getUserRole(),
          }}
        />
      )}

      <div className={classNames(window.stickyNavigation && applyStickyNav())}>
        {overlay && isMinDesktop && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(0, 0, 0, 0.5)",
              zIndex: 1000,
            }}
          />
        )}
        {topBar && (
          <PageHeaderElement className={"scha__logo-header"}>
            {!showDebugLanguage && <MediasLogo variant={mediasVariant} href={true} />}
            {showDebugLanguage && <DebugLanguageToggle language={CURRENT_LANGUAGE} />}

            {isMinTablet && (
              <>
                <Divider sx={{ margin: "auto -8px", height: 48 }} orientation={"vertical"} flexItem />
                {loggedIn && effectiveUser && <UserNavigation user={effectiveUser} showEdit={showEdit} />}
                {!loggedIn && <HeaderLoginElement />}

                <LanguageSelector />

                {isMinTablet && isMinDesktop && (
                  <Box sx={{ flex: 1 }}>
                    <SearchBox
                      recentSearches={recentSearches}
                      showRecentSearches={showRecentSearches}
                      hideRecentSearches={hideRecentSearches}
                      suggestions={suggestions}
                      showSuggestions={showSuggestions}
                      hideSuggestions={hideSuggestions}
                      isSearchPage={isSearchPage}
                      overlay={overlay}
                      showOverlay={showOverlay}
                      hideOverlay={hideOverlay}
                    />
                  </Box>
                )}
              </>
            )}
            {/* removed becuase of CR SCHAEFF-36763 */}
            {/* {screenSize >= useNumericScreenSize.desktopS && <DesktopScanDMCTeaser />} */}

            <CompanyLogo className="scha__header__logo" />
          </PageHeaderElement>
        )}

        <PageHeaderElement ref={menuAnchorRef} className={"scha__header-menu"} zIndex={5}>
          {!isMinDesktop && (
            <>
              <MobileNavigationLeft className="scha__mobile-navigation__left">
                {!topBar && (
                  <IconButton color="primary" onClick={displayTopBar}>
                    <KeyboardArrowLeftIcon />
                  </IconButton>
                )}
                {isMinPhone && !isMinDesktop && topBar && categoryNavigation}
                <Box
                  sx={{
                    flex: 1,
                    marginRight: { phone: 2, "desktop-s": 0 },
                    width: { phone: "100%", "desktop-s": "auto" },
                    display: { phone: "flex", "desktop-s": "block" },
                    alignItems: { phone: "center", "desktop-s": "stretch" },
                  }}
                >
                  <SearchBox
                    recentSearches={recentSearches}
                    showRecentSearches={showRecentSearches}
                    hideRecentSearches={hideRecentSearches}
                    suggestions={suggestions}
                    showSuggestions={showSuggestions}
                    hideSuggestions={hideSuggestions}
                    topBar={topBar}
                    displayTopBar={displayTopBar}
                    hideTopBar={hideTopBar}
                    showOverlay={showOverlay}
                    hideOverlay={hideOverlay}
                    isSearchPage={isSearchPage}
                    overlay={overlay}
                  />
                </Box>
              </MobileNavigationLeft>

              {topBar && (
                <MobileNavigationRight className={"scha__mobile-navigation__right"}>
                  {isMinTablet && isMinPhone && <MobilePersonalNav />}
                  {isMinPhone && !isMinTablet && (
                    <Fragment>
                      {
                        <Link
                          className={classNames(
                            "scha__mobile-navigation__button scha__mobile-navigation__button--dmc",
                            userIsLoggedIn() && "scha__mobile-navigation__button--dmc--logged-in",
                          )}
                          variant={LinkType.NAVIGATION}
                          href={SCAN_DMC_PAGE}
                          ocs-tracking-aux-marker="access-icon"
                        >
                          <QrCodeScannerOutlinedIcon sx={{ color: "text.secondary" }} />
                        </Link>
                      }
                      {loggedIn && effectiveUser && <UserNavigation user={effectiveUser} showEdit={showEdit} />}
                      {!loggedIn && effectiveUser && <HeaderLoginElement />}
                    </Fragment>
                  )}
                </MobileNavigationRight>
              )}
            </>
          )}

          {isMinDesktop && (
            <>
              <div className="scha__header-menu__navigation">{categoryNavigation}</div>

              <div className="scha__header-menu__personal-nav">
                <DesktopPersonalNav />
              </div>
            </>
          )}
        </PageHeaderElement>

        {/* Tour is completely removed right now - see SCHAEFF-14767 */}

        <ScrollToTop />
      </div>
    </>
  );
};

function LegacyStaticPageHeader() {
  return (
    <div>
      <StaticPageHeader />
      <SimpeRenderServerSideContent selector={"#BreadcrumbSlot"} />
    </div>
  );
}

export function createHeaderNavigation(props: Props) {
  let header;
  if (isForkPage() || isDatasheetPage()) {
    header = <LegacyStaticPageHeader />;
  } else if (isCheckoutPage()) {
    header = <CheckoutHeader />;
  } else {
    header = <HeaderNavigation {...props} />;
  }

  return (
    <>
      {props.includeSessionExpirePopup !== false && <SessionExpirePopupContainer />}
      {header}
    </>
  );
}

function getUserRole() {
  if (userIsCustomer()) {
    return "Business";
  }
  if (userIsRegistered()) {
    return "Plus";
  }
  return "Anonymous";
}

const MobileNavigationLeft = styled("div")(({ theme }) => ({
  display: "flex",
  marginLeft: theme.spacing(-1.75),
  flex: 1,
}));

const MobileNavigationRight = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginLeft: "auto",
  "& > * + *": {
    marginLeft: theme.spacing(3),
  },
  ul: {
    paddingLeft: 0,
  },
  ".scha__mobile-navigation__button--dmc": {
    marginLeft: theme.spacing(-1),
    marginRight: theme.spacing(-3),
  },
  ".scha__mobile-navigation__button--dmc--logged-in": {
    marginLeft: theme.spacing(-1),
    marginRight: theme.spacing(-2),
  },
}));
